var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import { useEffect, useState, useCallback, useRef } from "react";
import { useFocusable } from "../../utils/Helpers";
import { generateSource, getNextEpisode, updateProgress, } from "../../screens/Player/PlayerService";
import { useRouter } from "../../context/Router";
import { EpisodeListView } from "../views/episode-list/EpisodeListView";
import { focusManager } from "../../utils/Focus";
import { PlayerResolutionList } from "./PlayerResolutionList";
import { PlayerDubbingList } from "./PlayerDubbingList";
export var PlayerControls = function (_a) {
    var _b;
    var playerState = _a.playerState, setPlayerState = _a.setPlayerState, videoRef = _a.videoRef, playerRef = _a.playerRef, closePlayer = _a.closePlayer;
    var navigate = useRouter().navigate;
    var _c = useState(null), popup = _c[0], setPopup = _c[1];
    var _d = useState(""), showList = _d[0], setShowList = _d[1];
    var skipAccumulator = useRef(0);
    var skipTimeoutRef = useRef(null);
    useEffect(function () {
        focusManager.updateGroupOrder();
        focusManager.registerGroup({ name: "playerClose", position: 5 });
        focusManager.registerGroup({ name: "controls", position: 10 });
        focusManager.manualFocus("controls", 0);
    }, []);
    useEffect(function () {
        if (playerState.ended &&
            playerState.movie &&
            playerState.movie.movieType === "series") {
            nextEpisode();
        }
    }, [playerState.ended]);
    useEffect(function () {
        if (showList === "episodes") {
            videoRef.current.pause();
        }
        else if (!playerState.playing) {
            videoRef.current.play();
        }
    }, [showList]);
    var playPause = useCallback(function () {
        if (!videoRef.current) {
            return;
        }
        setPlayerState(function (oldPlayerState) {
            var newState = __assign(__assign({}, oldPlayerState), { playing: true });
            if (videoRef.current.paused) {
                videoRef.current.play();
            }
            else {
                videoRef.current.pause();
                newState.playing = false;
            }
            return newState;
        });
    }, []);
    var skip = useCallback(function (seconds) {
        if (!videoRef.current) {
            return;
        }
        if (!Number.isFinite(seconds)) {
            console.warn("Invalid skip value: not a finite number", seconds);
            return;
        }
        if (videoRef.current.duration - videoRef.current.currentTime < 10 &&
            seconds > 0) {
            return;
        }
        skipAccumulator.current += seconds;
        if (skipTimeoutRef.current) {
            clearTimeout(skipTimeoutRef.current);
        }
        skipTimeoutRef.current = setTimeout(function () {
            var currentTime = videoRef.current.currentTime;
            var duration = videoRef.current.duration;
            var maxDuration = duration - 10;
            if (!Number.isFinite(currentTime) || !Number.isFinite(duration)) {
                console.warn("Invalid video time or duration");
                skipAccumulator.current = 0;
                return;
            }
            var newTime = currentTime + skipAccumulator.current;
            newTime = Math.max(0, Math.min(newTime, maxDuration));
            try {
                videoRef.current.currentTime = newTime;
            }
            catch (error) {
                console.error("Failed to set video time:", error);
            }
            skipAccumulator.current = 0;
            skipTimeoutRef.current = null;
        }, 500);
    }, []);
    var nextEpisode = useCallback(function () {
        if (!playerState.loaded)
            return;
        var movie = playerState.movie;
        if (!movie || !movie.episodes || !movie.episodes.length)
            return;
        var next = getNextEpisode(movie.episodes, playerState.episodeId);
        if (!next || next.isLast)
            return;
        setPlayerState(function (oldPlayerState) { return (__assign(__assign({}, oldPlayerState), { loading: true, loaded: false })); });
        navigate("watch", {
            slug: movie.slug,
            episode: next.id,
        });
    }, [playerState.loaded, playerState.movie, playerState.episodeId]);
    var togglePopUp = useCallback(function (name) {
        if (name === "caption") {
            if (playerState.textTracks && playerState.textTracks.length === 0) {
                setPopup(null);
                return;
            }
        }
        setPopup(function (oldPopup) {
            var newPopup = oldPopup === name ? null : name;
            if (newPopup && newPopup.length > 0) {
                focusManager.pushLayer({ groupName: "playerPopup", index: 0 });
            }
            else {
                focusManager.popLayer();
            }
            return newPopup;
        });
    }, [playerState.textTracks]);
    var setCaption = useCallback(function (caption) {
        togglePopUp("caption");
        setPlayerState(function (oldPlayerState) { return (__assign(__assign({}, oldPlayerState), { caption: caption.lang })); });
        setPopup(null);
        updateProgress(videoRef, __assign(__assign({}, playerState), { caption: caption.lang }));
        caption.active = true;
        playerRef.current.enableTextTrack(caption.id);
    }, [playerState, togglePopUp]);
    var setDubbing = useCallback(function (dubbing) {
        var newState = __assign(__assign({}, playerState), { language: dubbing.lang });
        togglePopUp("dubbing");
        updateProgress(videoRef, newState);
        setPlayerState(newState);
        playerRef.current.selectAudioTrack(dubbing.id, true);
    }, [playerState, togglePopUp]);
    var setResolution = useCallback(function (resolution) {
        togglePopUp("resolution");
        setPlayerState(function (oldPlayerState) {
            if (resolution === oldPlayerState.quality) {
                return oldPlayerState;
            }
            if (!oldPlayerState.mappers) {
                return oldPlayerState;
            }
            var newSource = oldPlayerState.mappers
                ? generateSource(oldPlayerState.mappers, resolution.toUpperCase())
                : oldPlayerState.source;
            var newState = __assign(__assign({}, oldPlayerState), { quality: resolution, source: newSource !== null && newSource !== void 0 ? newSource : oldPlayerState.source });
            updateProgress(videoRef, newState);
            return newState;
        });
    }, [togglePopUp]);
    var playPauseRef = useFocusable({
        group: "controls",
        index: 0,
        onKeyDown: playPause,
        onKeyBack: closePlayer,
    });
    var skipBackwardRef = useFocusable({
        group: "controls",
        index: 1,
        onKeyDown: function () { return skip(-10); },
        onKeyBack: closePlayer,
    });
    var skipForwardRef = useFocusable({
        group: "controls",
        index: 2,
        onKeyDown: function () { return skip(10); },
        onKeyBack: closePlayer,
    });
    var nextEpisodeRef = useFocusable({
        group: "controls",
        index: 3,
        onKeyDown: nextEpisode,
        onKeyBack: closePlayer,
    });
    var episodeListRef = useFocusable({
        group: "controls",
        index: 4,
        onKeyDown: function () {
            focusManager.pushLayer({ groupName: "episodeListClose", index: 0 });
            setShowList("episodes");
        },
        onKeyBack: closePlayer,
    });
    var captionListRef = useFocusable({
        group: "controls",
        index: 5,
        onKeyDown: function () { return togglePopUp("caption"); },
        onKeyBack: closePlayer,
    });
    var audioListRef = useFocusable({
        group: "controls",
        index: 6,
        onKeyDown: function () { return togglePopUp("dubbing"); },
        onKeyBack: closePlayer,
    });
    var resolutionListRef = useFocusable({
        group: "controls",
        index: 7,
        onKeyDown: function () { return togglePopUp("resolution"); },
        onKeyBack: closePlayer,
    });
    var handleCloseEpisodeList = useCallback(function () {
        focusManager.clearGroupsAndResetOrder();
        focusManager.addGroupOrder("playerClose", 5);
        focusManager.addGroupOrder("controls", 10);
        focusManager.manualFocus("controls", 4);
        focusManager.popLayer();
        setShowList("");
    }, []);
    var movieType = playerState.movie ? playerState.movie.movieType : null;
    var isLastEpisode = getNextEpisode(((_b = playerState.movie) === null || _b === void 0 ? void 0 : _b.episodes) || [], playerState.episodeId).isLast;
    var nextEpisodeClass = "player-control";
    if (isLastEpisode) {
        nextEpisodeClass += " disabled";
    }
    return (React.createElement("div", { className: "controls", id: "controls" },
        showList.length > 0 && (React.createElement("div", { className: "modal-overlay", key: showList },
            React.createElement(EpisodeListView, { slug: playerState.movie ? playerState.movie.slug : "", episodes: playerState.movie ? playerState.movie.episodes : [], seasons: playerState.seasons, onClose: handleCloseEpisodeList, lastWatchedSeason: playerState.lastWatchedSeason, movieStatus: playerState.movie ? playerState.movie.status : null, currentEpisodeId: playerState.episodeId }))),
        React.createElement("div", { className: "left-container" },
            React.createElement("div", { className: "player-control", ref: playPauseRef, tabIndex: 0 },
                React.createElement("div", { className: playerState.playing ? "pause" : "play" })),
            React.createElement("div", { className: "player-control", ref: skipBackwardRef, tabIndex: 0 },
                React.createElement("div", { className: "skip-back" })),
            React.createElement("div", { className: "player-control", ref: skipForwardRef, tabIndex: 0 },
                React.createElement("div", { className: "skip-next" }))),
        playerState.movie && (React.createElement("div", { className: "movie-details" },
            React.createElement("p", { className: "movie-name" }, playerState.movie.name),
            playerState.movie.movieType === "series" &&
                playerState.episodeDetails && (React.createElement("p", { className: "episode-name" },
                React.createElement("span", null, playerState.episodeDetails.episodeNumber),
                " ",
                playerState.episodeDetails.episodeName)))),
        React.createElement("div", { className: "right-container " + (movieType || "") },
            movieType === "series" && (React.createElement("div", { className: nextEpisodeClass, ref: nextEpisodeRef, tabIndex: 0 },
                React.createElement("div", { className: "next" }))),
            movieType === "series" && (React.createElement("div", { className: "player-control", ref: episodeListRef, tabIndex: 0 },
                React.createElement("div", { className: "episode-list" }))),
            React.createElement(PlayerDubbingList, { language: playerState.language, popup: popup, audioTracks: playerState.audioTracks, onSetDubbing: setDubbing, onBackPress: function () { return togglePopUp("dubbing"); }, audioListRef: audioListRef }),
            React.createElement(PlayerResolutionList, { quality: playerState.quality, popup: popup, onSetResolution: setResolution, onBackPress: function () { return togglePopUp("resolution"); }, resolutionListRef: resolutionListRef }))));
};



//////////////////
// WEBPACK FOOTER
// ./src/components/player/PlayerControls.tsx
// module id = 194
// module chunks = 1